import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../api'
import DroneCard, { DroneCardData } from '../components/DroneCard'
import './Drones.css'

type DroneForm = {
  drone_id: string
  tail_number?: string
  phone_number?: string   // drone SIM card phone number
  sim_number?: string
  cellular_provider?: string
  notes?: string
  camera_stream_url?: string
  contact_name?: string
  contact_phone?: string
  contact_email?: string
  company_name?: string
  company_id?: string
  company_address?: string
}

const emptyForm: DroneForm = {
  drone_id: '',
  tail_number: '',
  phone_number: '',
  sim_number: '',
  cellular_provider: '',
  notes: '',
  camera_stream_url: '',
  contact_name: '',
  contact_phone: '',
  contact_email: '',
  company_name: '',
  company_id: '',
  company_address: '',
}

export default function Drones() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [showAdd, setShowAdd] = useState(false)
  const [form, setForm] = useState<DroneForm>(emptyForm)

  const { data: drones = [], isLoading, error } = useQuery<DroneCardData[]>({
    queryKey: ['drones'],
    queryFn: () => api.get('/drones').then((r) => r.data),
    refetchInterval: 5000, // live telemetry refresh every 5s
  })

  const createMutation = useMutation({
    mutationFn: (body: DroneForm) => api.post('/drones', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drones'] })
      setShowAdd(false)
      setForm(emptyForm)
    },
  })

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.drone_id.trim()) return
    createMutation.mutate({
      ...form,
      drone_id: form.drone_id.trim(),
      tail_number: form.tail_number?.trim() || undefined,
      phone_number: form.phone_number?.trim() || undefined,
      sim_number: form.sim_number?.trim() || undefined,
      cellular_provider: form.cellular_provider?.trim() || undefined,
      notes: form.notes?.trim() || undefined,
      contact_name: form.contact_name?.trim() || undefined,
      contact_phone: form.contact_phone?.trim() || undefined,
      contact_email: form.contact_email?.trim() || undefined,
      company_name: form.company_name?.trim() || undefined,
      company_id: form.company_id?.trim() || undefined,
      company_address: form.company_address?.trim() || undefined,
      camera_stream_url: form.camera_stream_url?.trim() || undefined,
    })
  }
  const set = (key: keyof DroneForm, value: string) => setForm((f) => ({ ...f, [key]: value }))

  const online = drones.filter((d) => ['online', 'active', 'flying'].includes(d.status)).length
  const total = drones.length

  return (
    <div className="drones">
      {/* ── Page header ── */}
      <div className="drones-header">
        <div className="drones-title-block">
          <h1>{t('drones.title')}</h1>
          <div className="drones-stats">
            <span className="ds-total">{total} רחפנים</span>
            {online > 0 && <span className="ds-online">● {online} מחוברים</span>}
          </div>
        </div>
        <button type="button" className="btn-primary" onClick={() => setShowAdd(true)}>
          + הוסף רחפן
        </button>
      </div>

      {/* ── Add form ── */}
      {showAdd && (
        <div className="drones-form-card">
          <h3>רחפן חדש</h3>
          <form onSubmit={handleAdd} className="drones-form-grid">
            <h4 className="form-section">פרטי רחפן</h4>
            <label>
              מזהה רחפן (חובה)
              <input
                value={form.drone_id}
                onChange={(e) => set('drone_id', e.target.value)}
                placeholder="DRONE-001"
                required
              />
            </label>
            <label>
              מספר זנב
              <input value={form.tail_number} onChange={(e) => set('tail_number', e.target.value)} placeholder="אופציונלי" />
            </label>
            <label>
              הערות
              <input value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="אופציונלי" />
            </label>
            <h4 className="form-section">📡 SIM / תקשורת</h4>
            <label>
              טלפון הרחפן (כרטיס SIM)
              <input
                value={form.phone_number}
                onChange={(e) => set('phone_number', e.target.value)}
                placeholder="+972-5X-XXX-XXXX"
                type="tel"
                dir="ltr"
              />
            </label>
            <label>
              מספר SIM
              <input value={form.sim_number} onChange={(e) => set('sim_number', e.target.value)} placeholder="ICCID / SIM ID" dir="ltr" />
            </label>
            <label>
              ספק סלולרי
              <input value={form.cellular_provider} onChange={(e) => set('cellular_provider', e.target.value)} placeholder="Partner / Hot / Cellcom" />
            </label>
            <h4 className="form-section">איש קשר</h4>
            <label>
              שם איש קשר
              <input value={form.contact_name} onChange={(e) => set('contact_name', e.target.value)} placeholder="שם מלא" />
            </label>
            <label>
              טלפון
              <input value={form.contact_phone} onChange={(e) => set('contact_phone', e.target.value)} placeholder="טלפון" type="tel" />
            </label>
            <label>
              אימייל
              <input value={form.contact_email} onChange={(e) => set('contact_email', e.target.value)} placeholder="דוא״ל" type="email" />
            </label>
            <h4 className="form-section">פרטי חברה</h4>
            <label>
              שם חברה
              <input value={form.company_name} onChange={(e) => set('company_name', e.target.value)} placeholder="שם החברה" />
            </label>
            <label>
              ח.פ. / ע.מ.
              <input value={form.company_id} onChange={(e) => set('company_id', e.target.value)} placeholder="מספר ח.פ. או ע.מ." />
            </label>
            <label className="form-full">
              כתובת חברה
              <input value={form.company_address} onChange={(e) => set('company_address', e.target.value)} placeholder="כתובת מלאה" />
            </label>
            <label className="form-full">
              כתובת זרם מצלמה (אופציונלי)
              <input
                value={form.camera_stream_url}
                onChange={(e) => set('camera_stream_url', e.target.value)}
                placeholder="https://… (HLS / MJPEG / MP4)"
                dir="ltr"
                style={{ fontFamily: 'ui-monospace, monospace' }}
              />
            </label>
            <div className="form-actions form-full">
              <button type="button" onClick={() => { setShowAdd(false); setForm(emptyForm) }}>
                ביטול
              </button>
              <button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'שומר...' : 'שמור'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── States ── */}
      {isLoading && <p className="drones-loading">טוען רחפנים...</p>}
      {error && <p className="drones-error">שגיאה בטעינת נתונים</p>}

      {/* ── Card grid ── */}
      {!isLoading && !error && (
        <>
          {drones.length === 0 ? (
            <div className="drones-empty-state">
              <span className="empty-icon">🚁</span>
              <p>אין רחפנים. הוסף רחפן באמצעות הכפתור למעלה.</p>
            </div>
          ) : (
            <div className="drones-grid">
              {drones.map((d) => (
                <DroneCard key={d.id} drone={d} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}
